import {
  Dispatch,
  SetStateAction,
  useEffect,
  useState,
  useCallback,
} from 'react';
import dayjs from 'dayjs';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import { useForm, Controller } from 'react-hook-form';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import Box from '@mui/material/Box';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import {
  CircularProgress,
  Divider,
  MenuItem,
  OutlinedInput,
  Radio,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { AddMemberTreeForm } from '../../../types/tree';
import { capitalise } from '../../../utils/capitalise';
import { getMemberNames } from '../../../utils/getMembers';
import { MemberId } from '../../../types/tree';
import { Person } from '../../../types/tree';
import { useAuth } from '../../../components/auth/AuthProvider';
import { MemberType } from '../../../types/tree';
import { sanitiseNameInput } from '../../../utils';
import { textLimits } from '../../../core/constants/constants';

type AddMemberDialogProps = {
  isAddMemberOpen: boolean;
  setIsAddMemberOpen: Dispatch<SetStateAction<boolean>>;
  familyType?: string;
  handleAddMember: (data: any) => void | Promise<any>;
  selectedPerson?: Person;
  treeMembers: any;
  loading: boolean;
  memberName: string;
};

export default function AddMemberDialog({
  isAddMemberOpen,
  setIsAddMemberOpen,
  familyType,
  handleAddMember,
  treeMembers,
  selectedPerson,
  loading,
  memberName,
}: AddMemberDialogProps) {
  const { currentUser } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingAnother, setIsSavingAnother] = useState(false);
  const [spouseNames, setSpouseNames] = useState<MemberId[]>([]);
  const [selectedSpouse, setSelectedSpouse] = useState<MemberId | null>(null);
  const [childrenNames, setChildrenNames] = useState<MemberId[]>([]);
  const [selectedChildren, setSelectedChildren] = useState<MemberId[]>([]);

  const getSuggestedGender = useCallback(
    (type?: string, person?: Person): 'male' | 'female' => {
      if (type === MemberType.FATHER) return 'male';
      if (type === MemberType.MOTHER) return 'female';
      if (type === MemberType.SPOUSE && person?.gender) {
        return person.gender === 'female' ? 'male' : 'female';
      }
      return 'male';
    },
    [],
  );

  const { control, handleSubmit, register, reset } = useForm<AddMemberTreeForm>(
    {
      defaultValues: {
        first_name: '',
        last_name: '',
        date_of_birth: null as any,
        gender: 'male',
      },
    },
  );

  useEffect(() => {
    if (isAddMemberOpen) {
      const suggestedGender = getSuggestedGender(familyType, selectedPerson);
      const prefilledLastName = selectedPerson?.last_name || '';
      setSelectedChildren([]);
      setSelectedSpouse(null);
      reset({
        first_name: '',
        last_name: prefilledLastName,
        date_of_birth: null as any,
        gender: suggestedGender,
      });
    }
  }, [familyType, selectedPerson, isAddMemberOpen, reset, getSuggestedGender]);

  useEffect(() => {
    if (treeMembers && isAddMemberOpen && selectedPerson) {
      const spouses = getMemberNames(
        treeMembers,
        selectedPerson?.spouse_id || [],
      );
      setSpouseNames(spouses);

      // Preselect the other parent/spouse when adding a child
      if (familyType === MemberType.CHILD) {
        if (spouses.length > 0) {
          setSelectedSpouse(spouses[0]);
        } else {
          setSelectedSpouse(null);
        }
      }

      const children = getMemberNames(
        treeMembers,
        selectedPerson?.children_id || [],
      );
      setChildrenNames(children);
    }
  }, [treeMembers, isAddMemberOpen, familyType, selectedPerson]);

  function buildPayload(data: AddMemberTreeForm) {
    return {
      member: {
        first_name: sanitiseNameInput(data.first_name),
        last_name: sanitiseNameInput(data.last_name),
        date_of_birth: data.date_of_birth || null,
        gender: data.gender,
        created_by: currentUser?.uid,
        ...(familyType === MemberType.SPOUSE
          ? {
              children_id: selectedChildren.map((child: MemberId) => child.id),
            }
          : null),
      },
      relation: {
        primary_user_id: selectedPerson?.id,
        primary_user_gender: selectedPerson?.gender,
        rel: familyType,
        ...(familyType === MemberType.CHILD
          ? {
              primary_spouse_id: selectedSpouse?.id,
              primary_spouse_gender: selectedSpouse?.gender,
              primary_children_id: selectedPerson?.children_id || [],
            }
          : null),
      },
    };
  }

  async function onRegularSubmit(data: AddMemberTreeForm) {
    const payload = buildPayload(data);
    setIsSubmitting(true);
    try {
      await Promise.resolve(handleAddMember(payload));
      setIsAddMemberOpen(false);
    } catch (e) {
      // Error handled by mutation/snackbar; keep dialog open
    } finally {
      setIsSubmitting(false);
    }
  }

  async function onSaveAndAddAnother(data: AddMemberTreeForm) {
    const payload = { ...buildPayload(data), _keepOpen: true };
    setIsSubmitting(true);
    setIsSavingAnother(true);
    try {
      await Promise.resolve(handleAddMember(payload));
      setSelectedChildren([]);
      if (familyType !== MemberType.CHILD) {
        setSelectedSpouse(null);
      }
      const suggestedGender = getSuggestedGender(familyType, selectedPerson);
      reset({
        first_name: '',
        last_name: selectedPerson?.last_name || '',
        date_of_birth: null as any,
        gender: suggestedGender,
      });
    } catch (e) {
      // Error handled by mutation/snackbar; keep dialog open
    } finally {
      setIsSubmitting(false);
      setIsSavingAnother(false);
    }
  }

  const isPending = loading || isSubmitting;
  // A person can only have one mother and one father
  const allowAddAnother =
    familyType !== MemberType.FATHER && familyType !== MemberType.MOTHER;

  return (
    <Dialog
      open={isAddMemberOpen}
      onClose={() => {
        if (!isPending) setIsAddMemberOpen(false);
      }}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ py: 2 }}>
        Add New Family Member - {capitalise(familyType || '')}
      </DialogTitle>
      <DialogContent>
        <form onSubmit={handleSubmit(onRegularSubmit)}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              autoFocus
              InputLabelProps={{ shrink: true }}
              label="First Name"
              {...register('first_name', { required: true })}
              required
              inputProps={{ maxLength: textLimits.personName }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSubmit(onRegularSubmit)();
                }
              }}
            />
            <TextField
              InputLabelProps={{ shrink: true }}
              label="Last Name"
              {...register('last_name', { required: true })}
              required
              inputProps={{ maxLength: textLimits.personName }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSubmit(onRegularSubmit)();
                }
              }}
            />
            <Controller
              name={'date_of_birth'}
              control={control}
              render={({ field }) => (
                <DatePicker
                  minDate={dayjs('1850-01-01').toDate()}
                  maxDate={dayjs().toDate()}
                  label="Date of Birth (Optional)"
                  format="dd/MM/yyyy"
                  value={field.value ? new Date(field.value as string) : null}
                  onChange={(date) =>
                    field.onChange(
                      date instanceof Date && !isNaN(date.valueOf())
                        ? date.toISOString()
                        : null,
                    )
                  }
                  slots={{ textField: TextField }}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              )}
            />
            <FormControl component="fieldset" required>
              <FormLabel>Gender</FormLabel>
              <Controller
                name={'gender'}
                control={control}
                render={({ field }) => (
                  <RadioGroup {...field} row value={field.value}>
                    <FormControlLabel
                      value="male"
                      control={<Radio />}
                      label="Male"
                    />
                    <FormControlLabel
                      value="female"
                      control={<Radio />}
                      label="Female"
                    />
                  </RadioGroup>
                )}
              />
            </FormControl>
            {familyType === MemberType.CHILD && (
              <>
                <Divider />
                <FormControl sx={{ my: 1 }}>
                  <FormLabel
                    sx={{ color: (theme) => theme.palette.text.primary }}
                  >
                    Select the other parent (optional)
                  </FormLabel>
                  <Typography variant="body2" py={1}>
                    If no parent is selected, the child will be added with only
                    one parent.
                  </Typography>
                  <Select
                    input={<OutlinedInput label="" />}
                    labelId="add-member-select-small-label"
                    id="add-member-select-small"
                    value={selectedSpouse?.id ?? ''}
                    label="Spouse"
                    onChange={(event: SelectChangeEvent) => {
                      const value = event.target.value;
                      if (value === '') setSelectedSpouse(null);
                      setSelectedSpouse(
                        spouseNames?.find(
                          (name: MemberId) => name.id === value,
                        ) || null,
                      );
                    }}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {spouseNames.map((member: MemberId) => (
                      <MenuItem key={member.id} value={member.id}>
                        {member.fullName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}
            {familyType === MemberType.SPOUSE && (
              <>
                <Divider />
                <FormControl sx={{ my: 1 }}>
                  <FormLabel
                    sx={{ color: (theme) => theme.palette.text.primary }}
                  >
                    Select the children with {memberName} (optional)
                  </FormLabel>
                  <Typography variant="body2" py={1}>
                    If no child is selected, the spouse will be added with no
                    children.
                  </Typography>
                  <Select
                    input={<OutlinedInput label="" />}
                    labelId="add-member-select-small-label"
                    id="add-member-multiple-checkbox"
                    multiple
                    value={selectedChildren?.map((member) => member.id) ?? []}
                    label="Children"
                    onChange={(event: SelectChangeEvent<string[]>) => {
                      const value = event.target.value;
                      const selectedIds =
                        typeof value === 'string' ? value.split(',') : value;
                      const selectedMembers = childrenNames.filter((member) =>
                        selectedIds.includes(member.id),
                      );
                      setSelectedChildren(selectedMembers);
                    }}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {childrenNames.map((member: MemberId) => (
                      <MenuItem key={member.id} value={member.id}>
                        {member.fullName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}
          </Box>
          <DialogActions sx={{ pt: 2, px: 0 }}>
            <Button
              variant="outlined"
              onClick={() => setIsAddMemberOpen(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            {allowAddAnother && (
              <Button
                type="button"
                variant="outlined"
                color="secondary"
                onClick={handleSubmit(onSaveAndAddAnother)}
                disabled={isPending}
                startIcon={
                  isSavingAnother ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : null
                }
              >
                {isSavingAnother ? 'Saving...' : 'Save & add another'}
              </Button>
            )}
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isPending}
              startIcon={
                isSubmitting && !isSavingAnother ? (
                  <CircularProgress size={16} color="inherit" />
                ) : null
              }
            >
              {isSubmitting && !isSavingAnother ? 'Adding...' : 'Add'}
            </Button>
          </DialogActions>
        </form>
      </DialogContent>
    </Dialog>
  );
}
